import * as Y from 'yjs';
import { MarkerType, type EdgeMarker } from '@xyflow/react';
import { migrateLegacyEdgeZOrder } from "@/features/canvas/engine/primitives/persistence/z-order";
import { canvasRoots, type CanvasRoots } from '../wiring/canvas-roots';
import { useCanvasBinding } from '../wiring/contexts/canvas-binding-context';
import { mapToObject, useYjsSnapshot } from '@/lib/sync/use-yjs-snapshot';

export interface RFCanvasEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  type?: string;
  data: Record<string, unknown>;
  /** Drag the source/target endpoint circles to re-anchor the edge.
   *  Required for RF's <EdgeUpdateAnchors> to render the drag handles.
   *  Disabled when the edge is locked. */
  reconnectable?: boolean;
  /** When false, RF refuses to delete this edge via Backspace/Delete. */
  deletable?: boolean;
  /** RF's serialised marker reference. Passing an EdgeMarker object lets
   *  RF auto-register the marker SVG and supply the URL to BaseEdge.
   *  Passing a raw string (e.g. 'url(#nula-marker-dot)') uses one of our
   *  custom <marker> defs. Undefined → no marker. */
  markerStart?: EdgeMarker | string;
  markerEnd?: EdgeMarker | string;
  /** Render order — derived from this id's position in doc.getArray('zOrder')
   *  (the unified node+edge stack). Higher value renders on top. */
  zIndex?: number;
  /** Invisible hit area in px around the edge path — widens click/hover
   *  target without changing the visible stroke. Forwarded to RF's edge wrapper. */
  interactionWidth?: number;
}

/** Encode a color string into a safe XML/CSS ID — replaces every non-alphanumeric char with its hex charcode. */
export function safeColorId(color: string): string {
  return color.replace(/[^a-zA-Z0-9]/g, (c) => `x${c.charCodeAt(0).toString(16)}`);
}

function resolveMarker(value: unknown, stroke: string): EdgeMarker | string | undefined {
  // Arrow markers are RF-generated. Passing `color` makes RF emit a
  // per-color <marker> variant whose fill matches the edge stroke, so
  // the arrowhead recolors with the line. Without `color`, RF reuses
  // its default arrow regardless of stroke.
  if (value === 'arrow') return { type: MarkerType.ArrowClosed, color: stroke, width: 20, height: 20 };
  // Dot markers use a per-color ID so each stroke color gets its own
  // <marker> def with fill hardcoded to that color. This avoids
  // `fill="context-stroke"` which is unsupported in WebKit (macOS Tauri).
  // RF wraps bare strings as url('#<id>') so we return just the id.
  // Encode the color to a safe alphanumeric ID — CSS.escape produces \#
  // sequences that break SVG url() references cross-browser.
  if (value === 'dot') return `nula-dot-${safeColorId(stroke)}`;
  return undefined;
}

/** Collect every unique dot-marker stroke color from the edges map so
 *  NulaEdge can register a <marker> def for each one. */
export function collectDotMarkerColors(roots: CanvasRoots): string[] {
  const seen = new Set<string>();
  roots.edges.forEach((edge) => {
    if (!(edge instanceof Y.Map)) return;
    const data = edge.get('data');
    if (!(data instanceof Y.Map)) return;
    if (data.get('markerStart') === 'dot' || data.get('markerEnd') === 'dot') {
      const stroke = data.get('stroke');
      seen.add(typeof stroke === 'string' ? stroke : 'var(--outline-variant)');
    }
  });
  return Array.from(seen);
}

function snapshotEdge(edge: Y.Map<unknown>): RFCanvasEdge | null {
  const id = edge.get('id');
  const source = edge.get('source');
  const target = edge.get('target');
  const kind = edge.get('kind');
  const data = edge.get('data');
  if (
    typeof id !== 'string' ||
    typeof source !== 'string' ||
    typeof target !== 'string'
  ) return null;
  const dataObj: Record<string, unknown> = data instanceof Y.Map ? mapToObject(data) : {};
  // Forward-fix: early edges were authored with stroke: 'var(--text-1)',
  // a token that doesn't exist in the design system. CSS resolves it to
  // the property's initial value (none) so those edges render invisibly.
  // Rewrite at read time to the real token so old docs still display.
  if (dataObj.stroke === 'var(--text-1)') {
    dataObj.stroke = 'var(--on-surface)';
  }
  // Surface drop-{dir} handle ids to React Flow so it knows which side of
  // each node to anchor the path to. createEdge.ts deliberately rewrites the
  // user's drag-source 'exp-{dir}' handle id to 'drop-{dir}' (the always-
  // mounted variant) before persisting, so any handle id we read here is
  // safe to forward. Old edges with stale 'exp-*' ids are upgraded at read
  // time so they still render after step-2 ships.
  // Locked edges can't be reconnected or deleted via the keyboard; the
  // floating toolbar collapses to a hold-to-unlock affordance. Mirror
  // the value into data so the toolbar (which reads via doc.getMap) can
  // detect the locked state without a separate hook.
  const locked = edge.get('locked') === true;
  if (locked) dataObj.locked = true;
  // Stroke fallback mirrors NulaEdge.DEFAULT_DATA — used both for the
  // BaseEdge stroke and for the arrow marker color so the arrowhead
  // tracks line color edits.
  const stroke = typeof dataObj.stroke === 'string' ? dataObj.stroke : 'var(--outline-variant)';
  return {
    id,
    source,
    target,
    sourceHandle: stableHandleId(edge.get('sourceHandle')),
    targetHandle: stableHandleId(edge.get('targetHandle')),
    type: typeof kind === 'string' ? kind : undefined,
    data: dataObj,
    reconnectable: !locked,
    deletable: !locked,
    markerStart: resolveMarker(dataObj.markerStart, stroke),
    markerEnd: resolveMarker(dataObj.markerEnd, stroke),
    interactionWidth: 40,
  };
}

function stableHandleId(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  // exp-{dir} is the cosmetic dot floating outside the node; drop-{dir}
  // is the on-edge anchor used as the path endpoint. Old edges may
  // reference exp-{dir} — upgrade them so paths render cleanly on the
  // node edge.
  if (value.startsWith('exp-')) return `drop-${value.slice(4)}`;
  return value;
}

export function useEdgesFromYjs(doc: Y.Doc): RFCanvasEdge[] {
  const binding = useCanvasBinding();
  const roots = canvasRoots(doc, binding);
  // One-shot read-time migration: fold any legacy 'edgeZOrder' entries
  // into the unified 'zOrder' array. Idempotent — re-runs as a no-op
  // once the doc has been migrated. Runs before the first derive so
  // initial render already sees the unified stack.
  migrateLegacyEdgeZOrder(doc, doc.clientID, binding);

  return useYjsSnapshot<RFCanvasEdge[]>(
    () => derive(roots),
    (refresh) => {
      const edgesMap = roots.edges;
      const zOrderArr = roots.zOrder;
      edgesMap.observeDeep(refresh);
      zOrderArr.observe(refresh);
      return () => {
        edgesMap.unobserveDeep(refresh);
        zOrderArr.unobserve(refresh);
      };
    },
    [doc],
  );
}

function derive(roots: CanvasRoots): RFCanvasEdge[] {
  const edgesMap = roots.edges;
  const order = roots.zOrder.toArray();
  // Build a position map so each edge knows where it sits in the unified
  // node+edge stack. Edges absent from the array (legacy docs whose
  // migration hasn't yet run, or transient races) get zIndex=-1 so they
  // render below everything else but stay visible.
  const orderIndex = new Map<string, number>();
  order.forEach((id, idx) => orderIndex.set(id, idx));

  const out: RFCanvasEdge[] = [];
  edgesMap.forEach((v) => {
    if (!(v instanceof Y.Map)) return;
    const snap = snapshotEdge(v);
    if (!snap) return;
    const idx = orderIndex.get(snap.id);
    snap.zIndex = idx !== undefined ? idx : -1;
    out.push(snap);
  });
  // Sort by zIndex so RF renders in stack order (lowest first → highest
  // on top). Stable across map-iteration order changes.
  out.sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));
  return out;
}
